import { pic } from "@/lib/responsive-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { WaveDivider } from "@/components/WaveDivider";
import { Breadcrumbs } from "@/components/Breadcrumbs";
const ctaHome = pic("cta-home");
const faqHero = pic("faq-hero");
import { useI18n, type Lang } from "@/lib/i18n";
import { useHeroFade } from "@/hooks/use-hero-fade";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "Roofing & Solar FAQ | AMR 3 Design & Construction" },
      {
        name: "description",
        content:
          "Answers about roofing replacement timelines, solar integration, warranties, financing, insurance claims, and emergency service in New Mexico.",
      },
      { property: "og:title", content: "Roofing & Solar FAQ | AMR 3" },
      {
        property: "og:description",
        content:
          "Common questions about roofing, solar integration, warranties, and insurance — answered.",
      },
      { property: "og:url", content: "https://www.amr3designconstruction.com/faq" },
      { property: "og:image", content: "https://www.amr3designconstruction.com/og-faq.jpg" },
      { property: "og:image:secure_url", content: "https://www.amr3designconstruction.com/og-faq.jpg" },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Roofing & Solar FAQ — AMR 3" },
      { name: "twitter:image", content: "https://www.amr3designconstruction.com/og-faq.jpg" },
      { name: "twitter:title", content: "Roofing & Solar FAQ | AMR 3" },
      { name: "twitter:description", content: "Common questions about roofing, solar integration, warranties, and insurance — answered." },
    ],
    links: [{ rel: "canonical", href: "https://www.amr3designconstruction.com/faq" }],
  }),
  component: FAQPage,
});

const faqs: Record<Lang, { q: string; a: string }[]> = {
  en: [
    {
      q: "What services does AMR 3 Design & Construction offer?",
      a: "We provide full-service roofing and solar integration for residential and commercial properties — including roof installation, replacement, repair, solar panel installation, integrated solar roofing, storm damage restoration, gutters, and insurance claim assistance across New Mexico.",
    },
    {
      q: "How does the roofing and solar process work?",
      a: "Every project starts with a no-obligation on-site consultation where we assess your roof, discuss your goals, and recommend the right system. We then provide a clear written proposal, handle all permits and HOA submissions, and our in-house crews complete the installation on schedule and on budget.",
    },
    {
      q: "How long does a typical roof replacement take?",
      a: "Most residential roof replacements are completed in 1–3 days, weather permitting. Larger or commercial projects may take longer. We provide a clear timeline upfront and keep you updated at every stage.",
    },
    {
      q: "Can I add solar to my existing roof?",
      a: "Yes. We assess your roof's age and condition first. If it has 10+ years of life left, we can install solar directly. Otherwise, we recommend replacing the roof and installing solar in one integrated project — saving you money, time and warranty headaches.",
    },
    {
      q: "What warranties do you offer?",
      a: "We back every project with a workmanship warranty plus full manufacturer warranties on roofing and solar materials, and a leak-free / performance guarantee on the integrated system. All warranty documentation is included with your proposal.",
    },
    {
      q: "Do you handle insurance claims?",
      a: "Absolutely. We document the damage with photos and reports, coordinate directly with your insurance adjuster, file the paperwork and can bill insurance directly for covered storm and hail damage.",
    },
    {
      q: "What areas do you serve?",
      a: "We're based in Albuquerque and serve residential and commercial customers across New Mexico — including Rio Rancho, Santa Fe, Los Lunas, Las Cruces and surrounding communities.",
    },
    {
      q: "Do you offer consultations and emergency service?",
      a: "Yes — every estimate comes with no obligation. For active leaks, storm damage or exposed roofing, call (505) 868-0847 24/7 and we'll dispatch as quickly as possible.",
    },
    {
      q: "Are you licensed, bonded and insured?",
      a: "Fully licensed, bonded and insured in the state of New Mexico, with comprehensive liability and workers' compensation coverage. Documentation is provided with every proposal.",
    },
    {
      q: "How can I contact you?",
      a: "Call us at (505) 868-0847, email us from our Contact page, or use the contact form to request a consultation. We typically reply the same business day.",
    },
  ],
  es: [
    {
      q: "¿Qué servicios ofrece AMR 3 Design & Construction?",
      a: "Ofrecemos servicios completos de techado e integración solar para propiedades residenciales y comerciales — incluyendo instalación, reemplazo y reparación de techos, instalación de paneles solares, techos solares integrados, restauración por daños de tormenta, canaletas y asistencia con reclamos de seguro en todo Nuevo México.",
    },
    {
      q: "¿Cómo funciona el proceso de techado y solar?",
      a: "Cada proyecto comienza con una consulta en sitio sin compromiso donde evaluamos su techo, discutimos sus objetivos y recomendamos el sistema adecuado. Luego entregamos una propuesta clara por escrito, gestionamos los permisos y nuestros equipos internos completan la instalación a tiempo y dentro del presupuesto.",
    },
    {
      q: "¿Cuánto tarda un reemplazo de techo?",
      a: "La mayoría de los techos residenciales se reemplazan en 1–3 días, dependiendo del clima. Proyectos comerciales o más grandes pueden tomar más tiempo. Siempre damos un cronograma claro.",
    },
    {
      q: "¿Puedo agregar solar a mi techo actual?",
      a: "Sí. Primero evaluamos la edad y condición del techo. Si tiene 10+ años de vida útil, instalamos solar directamente. Si no, recomendamos reemplazar el techo e instalar solar en un solo proyecto integrado para ahorrar tiempo y dinero.",
    },
    {
      q: "¿Qué garantías ofrecen?",
      a: "Garantía de mano de obra, garantías completas del fabricante en materiales de techo y solar, y garantía de techo sin filtraciones y rendimiento solar. Toda la documentación se incluye con la propuesta.",
    },
    {
      q: "¿Manejan reclamos de seguro?",
      a: "Sí. Documentamos los daños con fotos e informes, coordinamos directamente con su ajustador, presentamos el papeleo y podemos facturar directamente al seguro por daños cubiertos.",
    },
    {
      q: "¿Qué áreas atienden?",
      a: "Estamos en Albuquerque y servimos a clientes residenciales y comerciales en todo Nuevo México — Rio Rancho, Santa Fe, Los Lunas, Las Cruces y comunidades cercanas.",
    },
    {
      q: "¿Ofrecen consultas y servicio de emergencia?",
      a: "Sí — cada estimado es sin obligación. Para filtraciones activas, daños por tormenta o techos expuestos, llame al (505) 868-0847 las 24/7 y respondemos lo más rápido posible.",
    },
    {
      q: "¿Tienen licencia, fianza y seguro?",
      a: "Totalmente licenciados, asegurados y con fianza en Nuevo México, con cobertura completa de responsabilidad civil y compensación laboral. Documentación incluida en cada propuesta.",
    },
    {
      q: "¿Cómo puedo contactarlos?",
      a: "Llame al (505) 868-0847, escríbanos desde nuestra página de Contacto o use el formulario de contacto para solicitar una consulta. Generalmente respondemos el mismo día hábil.",
    },
  ],
};

function FAQPage() {
  const { t, lang } = useI18n();
  const heroRef = useHeroFade<HTMLElement>();
  const items = faqs[lang];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {/* Hero header — image-backed with sand wave (matches other pages) */}
      <section ref={heroRef} className="relative isolate overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28 will-change-[opacity]">
        <img
          src={faqHero.src}
          srcSet={faqHero.srcSet}
          sizes="100vw"
          alt="AMR 3 Design & Construction contractor explaining roofing and solar options to a homeowner couple in front of a New Mexico home at golden hour"
          width={1920}
          height={1080}
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[oklch(0.18_0.03_50/0.80)] via-[oklch(0.18_0.03_50/0.58)] to-[oklch(0.18_0.03_50/0.30)]" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={[{ label: t("nav.home"), to: "/" }, { label: t("nav.faq") }]} />
          <p className="mb-4 inline-block rounded-full border border-accent/40 bg-accent/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-accent backdrop-blur">
            {t("faq.badge")}
          </p>
          <h1 className="font-serif text-4xl font-bold leading-tight text-white drop-shadow sm:text-5xl lg:text-[3.25rem]">
            {t("faq.h1")}
          </h1>
          <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
            <Button asChild variant="gold" size="xl" className="hover-pulse-gold w-full sm:w-auto">
              <a href="tel:+15058680847" className="gap-2">
                <Phone className="h-4 w-4" /> {t("common.callPhone")}
              </a>
            </Button>
            <Button asChild variant="whiteOutline" size="xl" className="w-full sm:w-auto">
              <Link to="/contact" hash="project-form">{t("common.requestConsultation")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Accordion list — bottom-border style, centered */}
      <section className="bg-background py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="mb-8 text-center">
            <h2 className="font-serif text-3xl font-bold text-primary sm:text-4xl">
              {lang === "es" ? "Respuestas sobre techos y energía solar" : "Roofing & Solar Answers"}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              {lang === "es"
                ? "Desde inspecciones gratuitas y reclamos de seguro hasta garantías, permisos y tiempos de instalación: aquí respondemos las preguntas que más escuchamos de propietarios en Albuquerque, Rio Rancho y todo Nuevo México. Si su situación es diferente, llámenos y le daremos una respuesta clara y honesta."
                : "From free inspections and insurance claims to warranties, permits and installation timelines, these are the questions we hear most from homeowners in Albuquerque, Rio Rancho and across New Mexico. If your situation is different, give us a call and we'll give you a clear, honest answer."}
            </p>
            <p className="mt-3 text-xs text-muted-foreground">
              {lang === "es" ? "Actualizado: " : "Last updated: "}
              <time dateTime="2026-10-15">{lang === "es" ? "15 de octubre de 2026" : "October 15, 2026"}</time>
            </p>
          </div>
          <Accordion type="single" collapsible className="divide-y divide-border">
            {items.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-0 px-1">
                <AccordionTrigger className="py-5 text-left font-serif text-base font-semibold text-primary hover:no-underline sm:text-lg">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-14 text-center">
            <p className="text-sm text-muted-foreground">{t("faq.still")}</p>
            <div className="mt-4 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3">
              <Button asChild variant="gold" size="xl" className="hover-pulse-gold w-full sm:w-auto">
                <a href="tel:+15058680847" className="gap-2">
                  <Phone className="h-4 w-4" /> {t("common.callPhone")}
                </a>
              </Button>
              <Button asChild variant="goldOutline" size="xl" className="w-full sm:w-auto">
                <Link to="/contact" hash="project-form">{t("common.requestConsultation")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Dark CTA — image-backed, matches home pattern */}
      <section className="relative isolate overflow-hidden py-16 text-primary-foreground sm:py-24">
        <img
          src={ctaHome.src}
          srcSet={ctaHome.srcSet}
          sizes="100vw"
          alt="Modern New Mexico home at twilight"
          width={1920}
          height={1280}
          loading="lazy"
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[oklch(0.14_0.03_50/0.81)] via-[oklch(0.14_0.03_50/0.72)] to-[oklch(0.14_0.03_50/0.48)]" />
        <div className="absolute left-0 right-0 top-0 z-10">
          <WaveDivider flip color="var(--background)" />
        </div>
        <div className="relative mx-auto max-w-4xl px-4 pt-12 text-center sm:px-6">
          <h2 className="font-serif text-3xl font-bold sm:text-4xl">
            {t("faq.cta.title")}
          </h2>
          <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3">
            <Button asChild variant="gold" size="xl" className="w-full sm:w-auto">
              <a href="tel:+15058680847" className="gap-2">
                <Phone className="h-5 w-5" /> {t("common.callPhone")}
              </a>
            </Button>
            <Button asChild variant="whiteOutline" size="xl" className="w-full sm:w-auto">
              <Link to="/contact" hash="project-form">{t("hero.cta1")}</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
